import { useState, useCallback, useEffect } from 'react';

export interface UseContextSelectionOptions {
  sphereId: string;
  scopeKey: string;
  defaultContextSystemName: string;
  /** Hard override — bypasses localStorage (e.g., a metaspace template that pins a context) */
  initialOverride?: string;
}

export interface UseContextSelectionReturn {
  activeContextSystemName: string;
  setActiveContextSystemName: (systemName: string) => void;
  resetToDefault: () => void;
}

function buildStorageKey(scopeKey: string, sphereId: string): string {
  return `ctx-sel:${scopeKey}:${sphereId}`;
}

function safeLocalStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Silent fallback for private browsing mode or storage quota exceeded
  }
}

function safeLocalStorageRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Silent fallback
  }
}

/**
 * Manages the active context selection for a (sphere, scope) pair.
 *
 * Resolution order on mount: initialOverride → localStorage → defaultContextSystemName.
 * Writes to localStorage on selection change so the choice persists across reloads.
 * Fail-safe: any localStorage error silently falls back to default.
 */
export function useContextSelection({
  sphereId,
  scopeKey,
  defaultContextSystemName,
  initialOverride,
}: UseContextSelectionOptions): UseContextSelectionReturn {
  const storageKey = buildStorageKey(scopeKey, sphereId);

  const resolveInitial = (): string => {
    if (initialOverride) return initialOverride;
    const stored = safeLocalStorageGet(storageKey);
    if (stored) return stored;
    return defaultContextSystemName;
  };

  const [activeContextSystemName, setActive] = useState<string>(resolveInitial);

  // When the override or sphere identity changes, re-resolve (override wins).
  useEffect(() => {
    if (initialOverride) {
      setActive(initialOverride);
    }
  }, [initialOverride]);

  // When the DEFAULT changes (e.g. the server-resolved schema/arc-stage default
  // arrives after mount), re-apply it — but never over an explicit override or
  // the user's persisted manual pick.
  useEffect(() => {
    if (initialOverride) return;
    if (safeLocalStorageGet(storageKey)) return;
    setActive(defaultContextSystemName);
  }, [defaultContextSystemName, initialOverride, storageKey]);

  const setActiveContextSystemName = useCallback(
    (systemName: string) => {
      setActive(systemName);
      safeLocalStorageSet(storageKey, systemName);
    },
    [storageKey]
  );

  const resetToDefault = useCallback(() => {
    setActive(defaultContextSystemName);
    safeLocalStorageRemove(storageKey);
  }, [defaultContextSystemName, storageKey]);

  return { activeContextSystemName, setActiveContextSystemName, resetToDefault };
}
